import { useClientAsyncInit } from '@statsig/react-bindings';
import { useEffect, useState } from 'react';

import type { GetProudPartnersCreatorsLogosResult } from '../../../apps/explore/components/CreatorPage/CreatorPage.types.ts';
import type {
  ContentItem,
  MarketSpecificContent,
} from '../../../apps/explore/config/content/types.ts';
import { getPaymentMethods } from '../../../apps/explore/dtos/own/api-helpers.ts';
import type { PaymentMethod } from '../../../apps/explore/dtos/own/types.ts';
import { getBtfQueryParameters } from '../../../apps/explore/utils/btf';
import { isSSR } from '../../../apps/explore/utils/helpers';
import type { SemboMarket } from '../../../apps/explore/utils/markets.ts';
import { getProudPartnersCreatorsLogos } from './../../../apps/explore/components/CreatorPage/CreatorPage.repository';
import { footerContent } from './../../../apps/explore/config/content/footer';
import { getContentForMarket } from './../../../apps/explore/config/content/util';

export const useStatsigClient = (market: SemboMarket | undefined) => {
  const statsigSdkKey =
    process.env.NEXT_PUBLIC_STATSIG_ID_PROD ??
    'client-1TcR39BspCjupk333PNurPmnDtKyb2PD1bIyyJldzyb';

  const statsigUser = isSSR()
    ? { custom: { btf: [] } }
    : {
        custom: {
          btf: [...getBtfQueryParameters(window.location.href)],
          marketHost: market?.host,
        },
      };

  const statsigOptions = {
    environment: {
      tier: process.env.NEXT_PUBLIC_STATSIG_ID_PROD
        ? 'production'
        : 'development',
    },
  };

  return useClientAsyncInit(statsigSdkKey, statsigUser, statsigOptions);
};

export const useContent = (
  market: SemboMarket | undefined,
  isContentSectionEnabled: boolean
): MarketSpecificContent<ContentItem[][]> | undefined => {
  if (!isContentSectionEnabled || !market) {
    return undefined;
  }

  const footerContentForMarket = getContentForMarket(
    market.host,
    footerContent
  );

  return footerContentForMarket;
};

export const usePaymentMethods = (
  market: SemboMarket | undefined,
  isPaymentMethodsSectionEnabled: boolean
) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    const data = async () => {
      if (market && isPaymentMethodsSectionEnabled) {
        const results = await getPaymentMethods(market, 'sembo-footer');

        setPaymentMethods(results);
      }
    };

    data();
  }, [market, isPaymentMethodsSectionEnabled]);

  return paymentMethods;
};

export const usePartners = (
  market: SemboMarket | undefined,
  isPartnersSectionEnabled: boolean
) => {
  const [partners, setPartners] = useState<GetProudPartnersCreatorsLogosResult>(
    []
  );

  useEffect(() => {
    const data = async () => {
      if (market && isPartnersSectionEnabled) {
        const results = await getProudPartnersCreatorsLogos(market, {});

        setPartners(results);
      }
    };

    data();
  }, [market, isPartnersSectionEnabled]);

  return partners;
};
